import type * as Phaser from 'phaser';
import { rgb24 } from '../sim/color.ts';
import { waterAppearance } from '../sim/environment/water.ts';
import { evaluateSun, type MapDocument } from '../sim/map/index.ts';
import { drawViewportGrid, drawWaterSheet } from './drawGrid.ts';

export type MapBackdropLighting = {
  usesShader: boolean;
  setSheetAppearance: (color: number, alpha: number) => void;
};

/**
 * Everything `applyMapBackdrop` reads. Both scenes call it from `update()`, so a still
 * camera must cost one string compare instead of a seafloor rect plus every grid line
 * (docs/PERFORMANCE.md, root cause 4).
 *
 * The map inputs are in here, not just the camera, so the gate cannot go stale: an undo of
 * a water or sun edit leaves the camera exactly where it was, and a camera-only signature
 * would have kept the old colours on screen until the view moved.
 */
export function backdropSignature(
  cam: Phaser.Cameras.Scene2D.Camera,
  map: MapDocument,
  options: { lighting?: MapBackdropLighting | null; showGrid?: boolean } = {},
): string {
  const { waterColor, gridColor } = map.look;
  const { skyLight } = evaluateSun(map.environment);

  return [
    cam.scrollX,
    cam.scrollY,
    cam.zoom,
    cam.width,
    cam.height,
    map.bounds.width,
    map.bounds.height,
    options.showGrid ?? true,
    options.lighting?.usesShader ?? false,
    skyLight,
    waterColor.r,
    waterColor.g,
    waterColor.b,
    waterColor.a,
    gridColor.r,
    gridColor.g,
    gridColor.b,
    gridColor.a,
  ].join(',');
}

/**
 * Authored sheet colour plus the seafloor grid / Graphics water sheet.
 * Play and the editor share this so look edits cannot drift.
 */
export function applyMapBackdrop(
  grid: Phaser.GameObjects.Graphics,
  waterSheet: Phaser.GameObjects.Graphics,
  cam: Phaser.Cameras.Scene2D.Camera,
  map: MapDocument,
  options: {
    lighting?: MapBackdropLighting | null;
    showGrid?: boolean;
  } = {},
): void {
  const look = map.look;
  options.lighting?.setSheetAppearance(rgb24(look.waterColor), look.waterColor.a);

  const seafloor = waterAppearance(evaluateSun(map.environment).skyLight).seafloor;
  const showGrid = options.showGrid ?? true;

  drawViewportGrid(
    grid,
    cam,
    map.bounds,
    seafloor,
    rgb24(look.gridColor),
    showGrid ? look.gridColor.a : 0,
  );

  if (options.lighting?.usesShader) {
    waterSheet.clear();
  } else {
    drawWaterSheet(waterSheet, cam, map.bounds, {
      seafloor,
      color: rgb24(look.waterColor),
      alpha: look.waterColor.a,
    });
  }
}
